/* ============================================================
   Senchado 2.0 — 読みもの「すべて」（全記事一覧）の k-way マージ
   仕様SSOT: docs/spec-yomimono-hub.md §1
   セクション: sections/senchado-blog-all.liquid（/pages/yomimono）
   断片      : templates/blog.feed.liquid（/blogs/{handle}?view=feed&page=N）
   契約テスト: tests/blog-all.test.mjs

   なぜマージなのか:
     Shopify の記事は1ブログにしか所属しない。6ブログ（約2,163記事）を
     1つの時系列に見せる方法は、各ブログの「新しい順」を先頭から突き合わせて
     取り出す k-way マージしかない。

   時系列の正しさを保証する条件（ここが本体）:
     取り出す直前に「未完了なのにバッファが空」のブログが1つでもあれば、
     **先にそのブログの次ページを取り寄せてから** 各バッファ先頭を比較する。
     これを飛ばすと「まだ読んでいないだけの、もっと新しい記事」を持つブログが
     比較から抜け落ち、並びが静かに壊れる（画面上は何も起きないので気づけない）。

   ページサイズを仮定しない:
     クライアントが依存してよいのは「届いたカード枚数」と data-pages だけ。
     paginate は範囲外ページをクランプするので「N枚返ってきたから次もN枚」は成り立たない
     （dougu / tea で実際に踏んだ）。完了判定は「0枚が返った」か「data-pages を使い切った」。

   失敗したブログは打ち切る:
     1ブログの fetch 失敗でフィード全体を殺さない。すでにバッファに載っている記事は
     捨てずに使い切り、そのブログの続きだけを諦める。

   公開API（window / document を参照しないので Node から import 可能）
     posFor(index)                    → 通算 index から役割番号 1..10
     chunkFor(index)                  → 通算 index から .scb-feed の束番号
     createFeedMerger(handles, fetch) → { take(n), hasMore(), sources }
     CYCLE                            → 10（.scb-card--1..10 の周期）
   ============================================================ */

(function () {
  'use strict';

  /* .scb-card--1..10 の周期。assets/senchado-main-blog.css の「形」がこの周期で決まる。
     PC（≥992px）では各役割に grid-row が直接指定されているので、1つの .scb-feed に
     11枚以上入れると11枚目が1枚目に重なる。だから10枚ごとに .scb-feed を作り足す。 */
  var CYCLE = 10;

  /* fillEmpty の再帰保険。ページを取り寄せても状態が進まない異常時に無限ループさせない。 */
  var FILL_GUARD = 64;

  function posFor(index) {
    return (index % CYCLE) + 1;
  }

  function chunkFor(index) {
    return Math.floor(index / CYCLE);
  }

  /* ------------------------------------------------------------
     純関数コア: k-way マージ（DOM も fetch も知らない）
     handles   : ブログハンドルの配列（優先順＝同時刻の並び順）
     fetchPage : (handle, page) => Promise<{ items: [{published:Number, ...}], pages: Number|null }>
                 items は「新しい順」で並んでいること（Shopify の blog.articles の既定）。
     ------------------------------------------------------------ */
  function createFeedMerger(handles, fetchPage) {
    var sources = [];
    for (var i = 0; i < handles.length; i++) {
      sources.push({
        handle: handles[i],
        buffer: [],
        nextPage: 1,
        pages: null,
        done: false,   // これ以上ページを取りに行かない
        failed: false  // fetch が落ちて打ち切った
      });
    }

    function loadNext(src) {
      if (src.done) return Promise.resolve(false);
      var page = src.nextPage;
      return Promise.resolve()
        .then(function () {
          return fetchPage(src.handle, page);
        })
        .then(function (res) {
          var items = (res && res.items) || [];
          var pages = res && typeof res.pages === 'number' && isFinite(res.pages) ? res.pages : null;
          src.pages = pages;
          src.nextPage = page + 1;
          for (var j = 0; j < items.length; j++) src.buffer.push(items[j]);
          // ページサイズは見ない。0枚が返ったか、data-pages を使い切ったら完了。
          if (items.length === 0 || (pages !== null && src.nextPage > pages)) src.done = true;
          return items.length > 0;
        })
        .catch(function () {
          // 打ち切り: 続きは諦める。すでに取れている記事は捨てない。
          src.done = true;
          src.failed = true;
          return false;
        });
    }

    /* 「未完了かつバッファ空」のブログを、比較の前に埋める。
       0枚ページは loadNext が done にするので通常1周で収束するが、保険で回数を切る。 */
    function fillEmpty(guard) {
      var left = typeof guard === 'number' ? guard : FILL_GUARD;
      var pending = sources.filter(function (s) {
        return !s.done && s.buffer.length === 0;
      });
      if (pending.length === 0 || left <= 0) return Promise.resolve();
      return Promise.all(pending.map(loadNext)).then(function () {
        return fillEmpty(left - 1);
      });
    }

    /* 各バッファ先頭のうち published が最大のものを1枚取り出す。
       同値は handles の並び順（先に書いたブログ）を優先＝決定的。 */
    function pick() {
      var best = null;
      for (var k = 0; k < sources.length; k++) {
        var s = sources[k];
        if (s.buffer.length === 0) continue;
        if (best === null || s.buffer[0].published > best.buffer[0].published) best = s;
      }
      return best ? best.buffer.shift() : null;
    }

    function take(n) {
      var out = [];
      function step() {
        if (out.length >= n) return Promise.resolve(out);
        return fillEmpty().then(function () {
          var item = pick();
          if (item === null) return out; // 全ブログ枯渇
          out.push(item);
          return step();
        });
      }
      return step();
    }

    function hasMore() {
      for (var k = 0; k < sources.length; k++) {
        if (sources[k].buffer.length > 0 || !sources[k].done) return true;
      }
      return false;
    }

    return { take: take, hasMore: hasMore, sources: sources };
  }

  var API = {
    CYCLE: CYCLE,
    posFor: posFor,
    chunkFor: chunkFor,
    createFeedMerger: createFeedMerger
  };

  if (typeof module !== 'undefined' && module.exports) module.exports = API;
  if (typeof window !== 'undefined') window.SenchadoBlogAll = API;

  /* ------------------------------------------------------------
     ここから下は DOM 層（Node からの import では動かさない）
     ------------------------------------------------------------ */
  if (typeof document === 'undefined' || typeof window === 'undefined') return;
  if (typeof window.fetch !== 'function' || typeof window.DOMParser !== 'function') return;

  /* 断片 HTML → { items, pages }。属性以外は一切当てにしない。 */
  function parseFragment(html) {
    var doc = new DOMParser().parseFromString(html, 'text/html');
    var root = doc.querySelector('[data-pages]');
    var pages = root ? parseInt(root.getAttribute('data-pages'), 10) : NaN;
    var cards = doc.querySelectorAll('.scb-card');
    var items = [];
    for (var i = 0; i < cards.length; i++) {
      var published = parseInt(cards[i].getAttribute('data-published'), 10);
      items.push({ published: isFinite(published) ? published : 0, node: cards[i] });
    }
    return { items: items, pages: isFinite(pages) && pages > 0 ? pages : null };
  }

  /* マージ後の通算順で役割番号を振り直す。
     見出しレベルは senchado-main-blog と同じ規則（役割1だけ h2・他は h3）に合わせる。
     断片は pos=1 で描かれている（抜粋を必ず持たせるため）ので、ここで降格させる。 */
  function applyPos(card, pos) {
    card.className = 'scb-card scb-card--' + pos;
    card.setAttribute('data-cv', 'blog_all_' + pos);
    var title = card.querySelector('.scb-card__title');
    if (!title) return;
    var want = pos === 1 ? 'H2' : 'H3';
    if (title.tagName === want) return;
    var doc = card.ownerDocument || document;
    var replaced = doc.createElement(want.toLowerCase());
    replaced.className = title.className;
    replaced.textContent = title.textContent;
    title.parentNode.replaceChild(replaced, title);
  }

  function boot(root) {
    if (!root || root.dataset.scbAllBooted === '1') return;
    root.dataset.scbAllBooted = '1';

    var feedsWrap = root.querySelector('[data-scb-all-feeds]');
    if (!feedsWrap) return;
    var skeleton = root.querySelector('[data-scb-all-skeleton]');
    var status = root.querySelector('[data-scb-all-status]');
    var moreBtn = root.querySelector('[data-scb-all-more]');

    var config = [];
    try {
      config = JSON.parse(root.getAttribute('data-blogs') || '[]');
    } catch (e) {
      config = [];
    }
    var handles = [];
    var urlOf = {};
    for (var i = 0; i < config.length; i++) {
      var c = config[i];
      if (c && c.handle && c.url && !urlOf[c.handle]) {
        handles.push(c.handle);
        urlOf[c.handle] = c.url;
      }
    }

    var batch = parseInt(root.getAttribute('data-batch'), 10);
    if (!isFinite(batch) || batch < 1) batch = 20;
    var emptyText = root.getAttribute('data-empty-text') || '';
    var errorText = root.getAttribute('data-error-text') || '';

    function fetchPage(handle, page) {
      var base = urlOf[handle];
      var url = base + (base.indexOf('?') === -1 ? '?' : '&') + 'view=feed&page=' + page;
      return fetch(url, { credentials: 'same-origin' })
        .then(function (res) {
          if (!res.ok) throw new Error('HTTP ' + res.status);
          return res.text();
        })
        .then(parseFragment);
    }

    var merger = createFeedMerger(handles, fetchPage);
    var rendered = 0;
    var busy = false;

    function feedAt(index) {
      var feeds = feedsWrap.querySelectorAll('.scb-all__feed');
      while (feeds.length <= index) {
        var el = document.createElement('div');
        el.className = 'scb-feed scb-all__feed';
        feedsWrap.appendChild(el);
        feeds = feedsWrap.querySelectorAll('.scb-all__feed');
      }
      return feeds[index];
    }

    function render(items) {
      for (var i = 0; i < items.length; i++) {
        var node = document.importNode(items[i].node, true);
        applyPos(node, posFor(rendered));
        feedAt(chunkFor(rendered)).appendChild(node);
        rendered++;
      }
      if (items.length > 0) notifyMotion();
    }

    /* assets/senchado-blog-motion.js は「読み込み時の observe(document)」と
       「shopify:section:load（e.target 配下を再 observe）」でしか監視対象を増やさない。
       この面のカードは全部あとから挿入するので、テーマ共通の再初期化イベントを
       このセクション要素から発火して拾わせる（motion.js はこのタスクでは編集不可）。
       他の購読者（header/sekki）は対象が居ないか冪等な再適用なので副作用はない。 */
    function notifyMotion() {
      try {
        root.dispatchEvent(new CustomEvent('shopify:section:load', { bubbles: true }));
      } catch (e) {
        /* CustomEvent 非対応 — モーション無しでも一覧は成立する */
      }
    }

    function allFailed() {
      var src = merger.sources;
      if (src.length === 0) return false;
      for (var i = 0; i < src.length; i++) {
        if (!src[i].failed) return false;
      }
      return true;
    }

    function settle() {
      busy = false;
      if (skeleton) skeleton.hidden = true;
      if (moreBtn) {
        moreBtn.disabled = false;
        moreBtn.removeAttribute('aria-busy');
        moreBtn.hidden = !merger.hasMore();
      }
      if (status) {
        var text = '';
        if (rendered === 0) text = allFailed() || handles.length === 0 ? errorText : emptyText;
        status.textContent = text;
        status.hidden = text === '';
      }
    }

    function load() {
      if (busy) return;
      busy = true;
      if (moreBtn) {
        moreBtn.disabled = true;
        moreBtn.setAttribute('aria-busy', 'true');
      }
      merger
        .take(batch)
        .then(function (items) {
          render(items);
          settle();
        })
        .catch(function () {
          settle();
        });
    }

    if (moreBtn) {
      moreBtn.addEventListener('click', function () {
        load();
      });
    }

    if (handles.length === 0) {
      settle();
      return;
    }

    if (skeleton) skeleton.hidden = false;
    load();
  }

  function bootAll(scope) {
    (scope || document).querySelectorAll('[data-scb-all]').forEach(function (el) {
      boot(el);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      bootAll(document);
    });
  } else {
    bootAll(document);
  }

  /* Theme Editor でセクションが再描画されたら、新しい DOM で組み直す。
     notifyMotion() が投げる同名イベントもここに戻ってくるが、boot() の
     data-scb-all-booted ガードで no-op になる（自分で自分を再起動しない）。 */
  document.addEventListener('shopify:section:load', function (e) {
    bootAll(e.target);
  });
})();
